import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Search, Plus } from 'lucide-react';
import { fmtMoney } from './recordUtils';
import '../nativeitems/NativeItems.css';

// Records (Native) landing — the entry point for the record pages that
// replace "open it in NetSuite" (tasks #62/#60). One search box over
// /api/global-search (same engine as Ctrl+K) with grouped, deep-linking
// results, plus a legend of what each record type covers.

const TYPE_INFO = [
  { tag: 'so:', label: 'Sales orders', example: 'so:181582', newPath: '/native-orders/new' },
  { tag: 'po:', label: 'Purchase orders', example: 'po:463398', newPath: '/native-purchase-orders/new' },
  { tag: 'wo:', label: 'Work orders', example: 'wo:27238', newPath: '/native-work-orders/new' },
  { tag: 'cust:', label: 'Customers', example: 'cust:barnes' },
  { tag: 'ven:', label: 'Vendors', example: 'ven:cmagnets' },
  { tag: 'item:', label: 'Items', example: 'item:RW21420' },
];

export default function RecordsHome() {
  // ?tag=so: (from the Records nav rows) preloads the type-scoped search.
  const [searchParams] = useSearchParams();
  const initialTag = searchParams.get('tag') || '';
  const [query, setQuery] = useState(initialTag);
  useEffect(() => {
    const tag = searchParams.get('tag');
    if (tag != null) setQuery(tag);
  }, [searchParams]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const debounceRef = useRef(null);
  const seqRef = useRef(0);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) {
      setGroups([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      const seq = ++seqRef.current;
      try {
        const { data } = await axios.get('/api/global-search', { params: { q } });
        if (seq !== seqRef.current) return;
        setGroups(data.groups || []);
        setError(null);
      } catch (err) {
        if (seq !== seqRef.current) return;
        setError(err.response?.data?.error || 'Search failed');
        setGroups([]);
      } finally {
        if (seq === seqRef.current) setLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const hasResults = groups.some((g) => g.results.length > 0);

  return (
    <div className="nit-page">
      <div className="nit-item-head" style={{ marginTop: 8 }}>
        <h1>Records (Native)</h1>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6 }}>
          {TYPE_INFO.filter((t) => t.newPath).map((t) => (
            <Link key={t.tag} to={t.newPath} className="nit-btn" style={{ textDecoration: 'none' }}>
              <Plus size={13} /> {t.label.replace(/s$/, '')}
            </Link>
          ))}
        </span>
      </div>
      <p className="nit-caption">
        Open any sales order, purchase order, work order, customer, vendor or item exactly as you
        would in NetSuite — served from the synced Supabase mirror, no NetSuite session needed.
        The same search lives behind <kbd>Ctrl</kbd>+<kbd>K</kbd> on every page.
      </p>

      <div className="nit-card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Search size={16} />
          <input
            autoFocus
            style={{ flex: 1, border: 'none', outline: 'none', fontSize: 15, background: 'transparent' }}
            placeholder="Search records… try so:181582, po:463398, cust:barnes, item:RW21420"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search records"
          />
          {loading && <span className="nit-muted">searching…</span>}
        </div>
      </div>

      {error && <div className="nit-error">{error}</div>}

      {query.trim().length >= 2 && !loading && !hasResults && !error && (
        <div className="nit-empty">No matches for “{query.trim()}”.</div>
      )}

      {groups.map((group) => (
        group.results.length > 0 && (
          <div className="nit-card" key={group.type}>
            <h3>
              {group.label}{' '}
              <span className="nit-muted" style={{ fontWeight: 400 }}>
                {group.total > group.results.length ? `${group.results.length} of ${group.total}` : group.total}
              </span>
            </h3>
            <div className="nit-table-wrap">
              <table className="nit-table">
                <tbody>
                  {group.results.map((row) => (
                    <tr key={row.id}>
                      <td style={{ width: '22%' }}>
                        {row.url
                          ? <Link to={row.url}><code>{row.number || row.title}</code></Link>
                          : <code>{row.number || row.title}</code>}
                      </td>
                      <td>{row.title || '—'}</td>
                      <td className="nit-muted">{row.status || ''}</td>
                      <td className="r">{row.amount != null ? fmtMoney(row.amount) : ''}</td>
                      <td className="nit-muted">{row.date || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      ))}

      {query.trim().length < 2 && (
        <div className="nit-grid">
          {TYPE_INFO.map((t) => (
            <div className="nit-card" key={t.tag}>
              <h3><code>{t.tag}</code> {t.label}</h3>
              <p className="nit-caption">e.g. <code>{t.example}</code> — or search without a prefix to hit everything.</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
